import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import Bot3DViewer from './Bot3DViewer';
import BotControlsOverlay from './BotControlsOverlay';

const BotPovPanel = ({
  isDeploying = false,
  selectedClass = 'op',
  username = 'StormBot_47',
  targetServer = 'mc.hypixel.net:25565',
  viewerPort = 3007,
  isViewerReady = false,
  onSendControl,
  onSendLook,
  onOpenMenu,
  onAddLog,
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const STORAGE_KEY = 'bot_pov_expanded';
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved !== null ? saved === 'true' : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isExpanded));
    } catch {
      // Ignore storage errors
    }
  }, [isExpanded]);
  const [activeSlot, setActiveSlot] = useState(0);
  const [isControlled, setIsControlled] = useState(false);

  // Position & Orientation
  const [pos, setPos] = useState({ x: 142.5, y: 64.0, z: -312.8 });
  const [yaw, setYaw] = useState(180.0);
  const [pitch, setPitch] = useState(-2.1);
  const [fps] = useState(60);

  const canvasRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const worldTickRef = useRef(0);
  const animFrameIdRef = useRef(null);

  // Handle pointer lock state changes
  useEffect(() => {
    const handleLockChange = () => {
      const canvasEl = canvasRef.current;
      const locked = Boolean(
        document.pointerLockElement &&
        (document.pointerLockElement === canvasEl ||
         document.pointerLockElement === canvasEl?.parentElement)
      );
      setIsControlled(locked);
    };
    document.addEventListener('pointerlockchange', handleLockChange);
    return () => document.removeEventListener('pointerlockchange', handleLockChange);
  }, []);

  // Free look via pointer lock when controlled
  useEffect(() => {
    const handleMouseMovePointerLock = (e) => {
      if (!isControlled) return;
      const dx = e.movementX || 0;
      const dy = e.movementY || 0;
      setYaw((prev) => {
        const updated = (prev + dx * 0.25) % 360;
        onSendLook?.((updated * Math.PI) / 180, (pitch * Math.PI) / 180);
        return updated;
      });
      setPitch((prev) => Math.max(-89, Math.min(89, prev - dy * 0.25)));
    };

    if (isControlled) {
      window.addEventListener('mousemove', handleMouseMovePointerLock);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMovePointerLock);
    };
  }, [isControlled, pitch, onSendLook]);

  const handleToggleControl = (e) => {
    e?.stopPropagation();
    if (document.pointerLockElement) {
      document.exitPointerLock?.();
      setIsControlled(false);
      onAddLog?.('bot', isEn ? 'Control released.' : 'Управление ботом освобождено.');
    } else {
      const targetEl = canvasRef.current?.parentElement || canvasRef.current;
      if (targetEl?.requestPointerLock) {
        targetEl.requestPointerLock();
      }
      setIsControlled(true);
      onAddLog?.('bot', isEn ? 'Control captured (WASD / Mouse). Press ESC to release.' : 'Управление ботом захвачено (WASD / Мышь). Нажмите ESC для выхода.');
    }
  };

  // Bot Actions
  const handleBotAction = (action) => {
    if (action === 'jump') {
      setPos((prev) => ({ ...prev, y: prev.y + 1.25 }));
      setTimeout(() => setPos((prev) => ({ ...prev, y: 64.0 })), 350);
      onAddLog?.('bot', `${username} совершил прыжок (Packet 0x0B Player Move).`);
      onSendControl?.('jump', true);
      setTimeout(() => onSendControl?.('jump', false), 250);
    } else if (action === 'sneak') {
      setPos((prev) => ({ ...prev, y: 62.8 }));
      setTimeout(() => setPos((prev) => ({ ...prev, y: 64.0 })), 500);
      onAddLog?.('bot', `${username} присел (Entity Action: START_SNEAKING).`);
      onSendControl?.('sneak', true);
      setTimeout(() => onSendControl?.('sneak', false), 350);
    } else if (action === 'hit') {
      onAddLog?.('bot', `${username} выполнил взмах рукой (Arm Animation Packet 0x1A).`);
      onSendControl?.('attack', true);
      setTimeout(() => onSendControl?.('attack', false), 200);
    } else if (action === 'look') {
      const nextYaw = (yaw + 90) % 360;
      setYaw(nextYaw);
      onAddLog?.('bot', `Поворот угла обзора: Yaw: ${nextYaw.toFixed(1)}°`);
      onSendLook?.((nextYaw * Math.PI) / 180, (pitch * Math.PI) / 180);
    }
  };

  // Keyboard shortcut listener for space / shift / WASD when panel is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Avoid intercepting inside inputs
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        handleBotAction('jump');
      } else if (e.key === 'Shift') {
        handleBotAction('sneak');
      }

      if (isControlled) {
        if (e.code === 'KeyW') onSendControl?.('forward', true);
        if (e.code === 'KeyS') onSendControl?.('back', true);
        if (e.code === 'KeyA') onSendControl?.('left', true);
        if (e.code === 'KeyD') onSendControl?.('right', true);
      }
    };

    const handleKeyUp = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (isControlled) {
        if (e.code === 'KeyW') onSendControl?.('forward', false);
        if (e.code === 'KeyS') onSendControl?.('back', false);
        if (e.code === 'KeyA') onSendControl?.('left', false);
        if (e.code === 'KeyD') onSendControl?.('right', false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [yaw, pitch, username, isControlled, onSendControl]);

  // Canvas 3D render loop
  const renderWorld = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    worldTickRef.current += 0.02;
    const w = canvas.width;
    const h = canvas.height;

    // Sky gradient
    const skyGrad = ctx.createLinearGradient(0, 0, 0, h);
    skyGrad.addColorStop(0, '#0c1020');
    skyGrad.addColorStop(0.5, '#162238');
    skyGrad.addColorStop(0.51, '#1b2a41');
    skyGrad.addColorStop(1, '#080c14');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, w, h);

    const horizonY = h / 2 + pitch * 2.2;
    const yawRad = (yaw * Math.PI) / 180;

    // Wireframe ground lines
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;

    for (let x = -w; x <= w * 2; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x + Math.sin(yawRad) * 40, h);
      ctx.lineTo(w / 2 + Math.sin(yawRad) * 120, horizonY);
      ctx.stroke();
    }

    for (let y = horizonY + 10; y <= h; y += (y - horizonY) * 0.35 + 8) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.restore();

    // Floating Target Node
    ctx.save();
    const blockX = w / 2 + Math.cos(yawRad + worldTickRef.current * 0.5) * 140;
    const blockY = horizonY + 30;

    ctx.fillStyle =
      selectedClass === 'op'
        ? 'rgba(139, 92, 246, 0.65)'
        : selectedClass === 'crash'
        ? 'rgba(255, 71, 87, 0.65)'
        : 'rgba(255, 165, 2, 0.65)';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.rect(blockX - 18, blockY - 18, 36, 36);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = '11px JetBrains Mono, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`Target Node [${selectedClass.toUpperCase()}]`, blockX, blockY - 26);
    ctx.restore();

    animFrameIdRef.current = requestAnimationFrame(renderWorld);
  }, [pitch, yaw, selectedClass]);

  useEffect(() => {
    if (!isViewerReady) {
      const canvas = canvasRef.current;
      if (canvas && canvas.parentElement) {
        canvas.width = canvas.parentElement.clientWidth;
        canvas.height = canvas.parentElement.clientHeight || 380;
      }
      animFrameIdRef.current = requestAnimationFrame(renderWorld);
    }
    return () => {
      if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current);
    };
  }, [isViewerReady, renderWorld]);

  // Handle Canvas Mouse Drag for free look
  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - lastMousePosRef.current.x;
    const dy = e.clientY - lastMousePosRef.current.y;
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };

    setYaw((prev) => {
      const updated = (prev + dx * 0.4) % 360;
      onSendLook?.((updated * Math.PI) / 180, (pitch * Math.PI) / 180);
      return updated;
    });
    setPitch((prev) => Math.max(-89, Math.min(89, prev - dy * 0.4)));
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  // Weapon slot icon matching selected role
  const getSlot1Icon = () => {
    if (selectedClass === 'op') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
          <path d="m13 19 6-6" />
          <path d="m16 16 5 5" />
          <path d="m19 21 2-2" />
        </svg>
      );
    } else if (selectedClass === 'crash') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    } else {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    }
  };

  return (
    <section>
      <div className="section-mono-header">
        <div className="section-mono-title">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
          </svg>
          <span>{isEn ? 'BOT POV VIEWPORT & HUD' : 'ПРЕДПРОСМОТР ОТ ЛИЦА БОТА (POV VIEWPORT)'}</span>
        </div>
      </div>

      <div className={`bot-pov-panel ${isExpanded ? 'expanded' : ''}`}>
        {/* 3-Column Header */}
        <div className="bot-pov-header" onClick={() => setIsExpanded(!isExpanded)}>
          <div className="bot-pov-left">
            <div className="pov-live-beacon hidden sm:inline-flex">
              <span className="pov-beacon-dot"></span>
              <span>LIVE POV</span>
            </div>
            <span className="pov-title">{isEn ? '1st Person View & HUD' : 'Вид от первого лица & HUD'}</span>
          </div>

          <div className="pov-meta-badges">
            <div className="pov-badge-item">
              <span>POS:</span>{' '}
              <strong className="pov-pos-val">
                X: {pos.x.toFixed(2)} Y: {pos.y.toFixed(2)} Z: {pos.z.toFixed(2)}
              </strong>
            </div>
            <div className="pov-badge-item">
              <span>FPS:</span> <strong>{fps}</strong>
            </div>
          </div>

          <button
            type="button"
            className="pov-toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            <span>{isExpanded ? (isEn ? 'Collapse' : 'Свернуть') : (isEn ? 'Expand' : 'Развернуть')}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
        </div>

        {/* Panel Body: Viewport + HUD + Controls */}
        <div className="bot-pov-body">
          <div className="pov-viewport-container">
            {isViewerReady && isDeploying ? (
              <Bot3DViewer
                viewerPort={viewerPort}
                isConnected={isDeploying}
                isViewerReady={isViewerReady}
                onSendControl={onSendControl}
                onSendLook={onSendLook}
                onOpenMenu={onOpenMenu}
              />
            ) : (
              <canvas
                ref={canvasRef}
                id="botPovCanvas"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
            )}

            {/* Minecraft HUD Overlay */}
            <div className="mc-hud-overlay">
              <div className="hud-top-bar">
                <div className="hud-debug-info">
                  <div>
                    Bot: <span style={{ color: '#2ed573' }}>{username}</span>
                  </div>
                  <div>
                    Target: <span style={{ color: '#ffa502' }}>{targetServer}</span>
                  </div>
                  <div>
                    Facing: North (Yaw: {yaw.toFixed(1)}, Pitch: {pitch.toFixed(1)})
                  </div>
                </div>

                <div className="hud-view-modes">
                  <button
                    type="button"
                    className={`hud-control-capture-btn ${isControlled ? 'active' : ''}`}
                    onClick={handleToggleControl}
                    title={isEn ? 'Capture bot controls (WASD, Mouse)' : 'Захватить управление ботом (WASD, мышь)'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="14" x="2" y="5" rx="2" />
                      <line x1="6" y1="12" x2="10" y2="12" />
                      <line x1="8" y1="10" x2="8" y2="14" />
                      <circle cx="15" cy="11" r="1" fill="currentColor" />
                      <circle cx="18" cy="13" r="1" fill="currentColor" />
                    </svg>
                    <span>{isControlled ? (isEn ? 'Release Control (ESC)' : 'Управление активно (ESC)') : (isEn ? 'Take Control' : 'Захват управления')}</span>
                  </button>
                </div>
              </div>

              {/* Crosshair */}
              <div className="hud-crosshair"></div>

              {/* Bottom Wrap: Hearts, Hunger, Hotbar */}
              <div className="hud-bottom-wrap">
                <div className="hud-stats-row">
                  <div className="hud-hearts">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <svg key={i} className="hud-heart-icon" viewBox="0 0 24 24">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                    ))}
                  </div>
                  <div className="hud-hunger">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <svg key={i} className="hud-hunger-icon" viewBox="0 0 24 24">
                        <path d="M18.5 3a4.5 4.5 0 0 0-4.5 4.5c0 .63.14 1.23.38 1.77L4.12 19.53a1.5 1.5 0 0 0 2.12 2.12L16.5 11.4c.54.24 1.14.38 1.77.38a4.5 4.5 0 0 0 4.5-4.5c0-2.48-1.79-4.28-4.27-4.28z" />
                      </svg>
                    ))}
                  </div>
                </div>

                {/* 9-Slot Minecraft Hotbar */}
                <div className="hud-hotbar">
                  <div
                    className={`hotbar-slot ${activeSlot === 0 ? 'active' : ''}`}
                    title="Слот 1: Орудие роли"
                    onClick={() => setActiveSlot(0)}
                  >
                    {getSlot1Icon()}
                  </div>
                  <div
                    className={`hotbar-slot ${activeSlot === 1 ? 'active' : ''}`}
                    title="Слот 2: Редстоун факел"
                    onClick={() => setActiveSlot(1)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                      <path d="M12 2v6" />
                      <rect x="8" y="8" width="8" height="12" rx="2" />
                      <circle cx="12" cy="5" r="2" fill="#ff4757" />
                    </svg>
                  </div>
                  <div
                    className={`hotbar-slot ${activeSlot === 2 ? 'active' : ''}`}
                    title="Слот 3: Книга эксплойтов"
                    onClick={() => setActiveSlot(2)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </div>
                  <div
                    className={`hotbar-slot ${activeSlot === 3 ? 'active' : ''}`}
                    title="Слот 4: Эндер-жемчуг"
                    onClick={() => setActiveSlot(3)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                      <circle cx="12" cy="12" r="8" />
                      <circle cx="12" cy="12" r="3" fill="#00d2d3" />
                    </svg>
                  </div>
                  <div
                    className={`hotbar-slot ${activeSlot === 4 ? 'active' : ''}`}
                    title="Слот 5: Компас"
                    onClick={() => setActiveSlot(4)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                    </svg>
                  </div>
                  <div
                    className={`hotbar-slot ${activeSlot === 5 ? 'active' : ''}`}
                    title="Слот 6: Пусто"
                    onClick={() => setActiveSlot(5)}
                  ></div>
                  <div
                    className={`hotbar-slot ${activeSlot === 6 ? 'active' : ''}`}
                    title="Слот 7: Пусто"
                    onClick={() => setActiveSlot(6)}
                  ></div>
                  <div
                    className={`hotbar-slot ${activeSlot === 7 ? 'active' : ''}`}
                    title="Слот 8: Золотое яблоко"
                    onClick={() => setActiveSlot(7)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                      <circle cx="12" cy="13" r="7" />
                      <path d="M12 6c0-2 2-3 4-3" />
                    </svg>
                  </div>
                  <div
                    className={`hotbar-slot ${activeSlot === 8 ? 'active' : ''}`}
                    title="Слот 9: Щит оператора"
                    onClick={() => setActiveSlot(8)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Real-time WASD and Touch Controls Overlay when live */}
          {isDeploying && onSendControl && (
            <div className="mt-3">
              <BotControlsOverlay
                onSendControl={onSendControl}
                isConnected={isDeploying}
              />
            </div>
          )}

          {/* Action Buttons Bar */}
          <div className="pov-actions-bar">
            <div className="pov-actions-left">
              <button
                type="button"
                className="pov-ctrl-btn"
                onClick={() => handleBotAction('jump')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
                <span className="pov-btn-text-full">{isEn ? 'Jump [Space]' : 'Прыжок [Space]'}</span>
                <span className="pov-btn-text-short">{isEn ? 'Jump' : 'Прыжок'}</span>
              </button>
              <button
                type="button"
                className="pov-ctrl-btn"
                onClick={() => handleBotAction('sneak')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M12 5v14M19 12l-7 7-7-7" />
                </svg>
                <span className="pov-btn-text-full">{isEn ? 'Sneak [Shift]' : 'Присесть [Shift]'}</span>
                <span className="pov-btn-text-short">{isEn ? 'Sneak' : 'Присед'}</span>
              </button>
              <button
                type="button"
                className="pov-ctrl-btn"
                onClick={() => handleBotAction('hit')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
                </svg>
                <span className="pov-btn-text-full">{isEn ? 'Hit [LMB]' : 'Удар [ЛКМ]'}</span>
                <span className="pov-btn-text-short">{isEn ? 'Hit' : 'Удар'}</span>
              </button>
            </div>

            <div className="pov-actions-hint">
              {isEn
                ? 'Hold LMB on canvas for free camera rotation'
                : 'Зажмите ЛКМ на холсте для свободного вращения камеры'}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BotPovPanel;
